import { useSuspenseQuery } from '@apollo/client';
import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  ColDefBuilder,
  DataTable,
  Heading,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import { graphql } from '@eluve/graphql.tada';

import { RemoveArtifactFromDatasetAction } from './RemoveArtifactFromDatasetAction';

const getDatasetDetailsQuery = graphql(`
  query getDatasetDetails($datasetId: uuid!) {
    evalDatasetsByPk(id: $datasetId) {
      __typename
      id
      name
      description
      createdAt
      datasetSourceArtifacts {
        __typename
        id
        createdAt
        sourceArtifact {
          __typename
          id
          name
          description
          content
        }
      }
    }
  }
`);

type DatasetArtifactRow = {
  id: string;
  name: string | null;
  description: string | null;
  content: string;
  createdAt: string;
  datasetSourceArtifactId: string;
};

export const DatasetDetailsPage: React.FC = () => {
  const { datasetId } = useParams() as {
    datasetId: string;
  };
  const toast = useToast();

  const artifactColumns = new ColDefBuilder<DatasetArtifactRow>()
    .linkSortable(
      'name',
      (row) => `/admin/fact-verification/artifacts/${row.id}`,
    )
    .defaultSortable('description')
    .defaultSortable('content', {
      cellRenderer: (row) => (
        <Text>
          {row.content.length > 300
            ? `${row.content.substring(0, 300)}...`
            : row.content}
        </Text>
      ),
    })
    .colDef({
      header: 'Actions',
      cell: ({
        row: {
          original: { datasetSourceArtifactId },
        },
      }) => (
        <RemoveArtifactFromDatasetAction
          datasetSourceArtifactId={datasetSourceArtifactId}
          datasetId={datasetId}
        />
      ),
    })
    .build();

  const {
    data: { evalDatasetsByPk: datasetData },
  } = useSuspenseQuery(getDatasetDetailsQuery, {
    variables: {
      datasetId,
    },
  });

  const artifactRows: DatasetArtifactRow[] = useMemo(
    () =>
      orderBy(
        datasetData?.datasetSourceArtifacts.map((d) => {
          return {
            id: d.sourceArtifact.id,
            name: d.sourceArtifact.name,
            description: d.sourceArtifact.description,
            content: d.sourceArtifact.content,
            createdAt: d.createdAt,
            datasetSourceArtifactId: d.id,
          };
        }),
        (m) => m.createdAt,
        ['desc'],
      ),
    [datasetData?.datasetSourceArtifacts],
  );

  if (!datasetData) {
    toast.error('Failed to load dataset');
    throw new Error('Failed to load dataset');
  }

  return (
    <Stack className="mt-4" gap={3} align="stretch">
      <div className="my-2 py-2">
        <Heading>{datasetData.name}</Heading>
        <Stack gap={2} align="stretch">
          <Text className="text-black">{datasetData.description}</Text>
          <Text>Created at: {formatToLocale(datasetData.createdAt)}</Text>
        </Stack>
      </div>

      <div>
        <Heading
          size="2xl"
          weight="semibold"
          className="pb-1 hover:cursor-pointer"
        >
          Source Artifacts
        </Heading>
        <DataTable columns={artifactColumns} data={artifactRows} />
      </div>
    </Stack>
  );
};
